/**
 * Queries to SharePoint REST Search API return non-intuitive JSON objects with search results.
 * This type parses the returned JSON into two collections: a flat list of primary results, and
 * a tree-like structure of search refiner filters.
 * */
export class QnAResultsParser {
  /**
   * Maps an array of SharePoint primary search results to an array of Questions and Answers.
   * @param results - an array of question and answer pairs.
   */
  public buildPrimaryResults(results): any[] {
    const primaryResults: any[] = results.PrimaryQueryResult.RelevantResults.Table.Rows;
    const qna = primaryResults.map(result => {
      return {
        question: this.getPropertyValue(result, 'Title'),
        answer: this.formatHitHighlights(this.getPropertyValue(result, 'HitHighlightedSummary')),
        detailedAnswer: this.getPropertyValue(result, 'QnAAnswerOWSMTXT'),
        documentLink: this.getPropertyValue(result, 'DocumentLink')
      };
    });

    return qna;
  }

  /**
   * Extracts values from SharePoint search results and builds a refiner tree structure.
   * @param results - a tree-like structure to be used as a data source of refiner tree.
   */
  public buildRefinerTree(results): any[] {
    // We are using a single refiner: "RefinableString05" managed property.
    const refiners: any[] = results.PrimaryQueryResult.RefinementResults.Refiners[0].Entries;
    const flatNodes: any[] = refiners.reduce(this.refinerReducer, []);
    const nodeMap = new Map<string, any>();
    flatNodes.forEach(node => { nodeMap.set(node.value, node); });
    let tree: any[] = [];

    nodeMap.forEach((item, key) => {
      let parentId = item['parent'];

      if (parentId != null) {
        let parent = nodeMap.get(parentId);
        parent['children'].push(item);
      } else {
        tree.push(item);
      }
    });

    return tree;
  }

  // Iterates over refiner results and converts them to a flat array of {name:'', value:''} elements.
  private refinerReducer = (tree: any[], item: any, index: number, refiners: any[]): any[] => {
    const values = this.splitRefinemenetValue(item.RefinementValue);
    const names = this.mapRefinementValuesToNames(Number(item.RefinementCount), values);
    const nodes = values.map((value, index, arr) => {
      let parent: string = null;
      if (index > 0) {
        parent = arr[index - 1];
      }

      return {
        parent: parent,
        value: value,
        name: names[index],
        children: []
      };
    });
    return [...tree, ...nodes];
  }

  // Expands refiner values from colon-delimited strings into array of hierarchical strings.
  // Ex. "GrandParent:Parent:Child" >> ["GrandParent", "GrandParent:Parent", "GrandParent:Parent:Child"]
  private splitRefinemenetValue(value: string): string[] {
    let values: string[] = [];
    let splitIndex = value.indexOf(':', 0);

    while (splitIndex > 0) {
      let text = value.substring(0, splitIndex);
      values.push(text);
      splitIndex = value.indexOf(':', splitIndex + 1);
    }

    values.push(value);
    return values;
  }

  // Takes in an array of refiner values and maps it to refiner names.
  private mapRefinementValuesToNames(refinementCount: number, values: string[]): string[] {
    const names = values.map(value => {
      const startIndex = value.lastIndexOf(':');

      if (startIndex > 0) {
        return value.substring(startIndex + 1);
      } else {
        return value;
      }
    });

    if (refinementCount > 0) {
      names[names.length - 1] = names[names.length - 1] + ' (' + refinementCount + ')';
    }

    return names;
  }

  // Applies rules specific to hit-highlighted markup and formats it as HTML.
  private formatHitHighlights(text: string): string {
    if (!text) {
      return text;
    }

    // <c0> and </c0> should be replaced with <strong>; but hold off on that for now.
    let richText = text.replace(/<c0>/mig, '');
    richText = richText.replace(/<\/c0>/mig, '');
    richText = richText.replace(/<ddd\/>/mig, '&#8230;');

    return richText;
  }

  private getPropertyValue(item: any, key: string): string {
    // Use of array.prototype.find() requires a polyfill for IE. Edge is OK.
    const property = item.Cells.find(item => item.Key === key);
    return property.Value;
  }
}
